import WButton from "../wbutton/WButton";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import LCountDownTimer from "../lcount-down-timer/LCountDownTimer";
import clsx from "clsx";
import parse from "html-react-parser";
import fbEvent from "@/utils/fbEvent";
import Image from "next/image";

/* eslint-disable-next-line */
export interface LHeroSectionProps {
  id?: string;
  topTitle?: string;
  mainTitle: string;
  secondaryTitle?: string;
  image: {
    url: string;
    altTag: string;
  };
  mobileImage?: {
    url: string;
    altTag: string;
  };
  button?: {
    text: string;
    url: string;
    urlParams?: string;
  };
  countDownTimer?: {
    endDate: Date;
    description?: string;
  };
  bgColor?: string;
  box?: boolean;
  boxColor?: string;
  textColor?: string;
  textShadow?: boolean;
}

export const LHeroSection = (props: LHeroSectionProps) => {
  const router = useRouter();

  const onClick = () => {
    const getAllCurrentParams = window.location.search;

    if (!props?.button?.url?.includes("#")) {
      const urlObject = new URL(props?.button?.url || "");
      const newTargetUrl = `${urlObject?.origin}${urlObject?.pathname}`;

      const urlSearchParams = urlObject.searchParams.toString();

      // Adjust combining search parameters based on getAllCurrentParams
      let combinedSearchParams;
      if (getAllCurrentParams) {
        combinedSearchParams =
          getAllCurrentParams + (urlSearchParams ? "&" + urlSearchParams : "");
      } else {
        combinedSearchParams = urlSearchParams ? "?" + urlSearchParams : "";
      }

      const newUrlWithParams = `${newTargetUrl}${combinedSearchParams}`;

      router?.push(newUrlWithParams);
    } else {
      router?.push(props?.button?.url);
    }
  };

  return (
    <div
      id={props?.id}
      className={clsx([
        `relative w-full pt-5 pb-10 lg:pb-0 bg-none`,
        !props?.bgColor && "bg-ekblue",
        "hero-section",
      ])}
      style={{
        backgroundColor: props?.bgColor ? props?.bgColor : undefined,
      }}
    >
      {props.image.url && (
        <div className="absolute inset-0 z-0 overflow-hidden">
          <Image
            src={props.image.url}
            alt={props.image.altTag}
            fill
            priority={true}
            sizes="100vw"
            style={{ objectFit: "cover" }}
            quality={85}
          />
        </div>
      )}
      <div className="container z-20 flex flex-col gap-10 px-4 py-5 mx-auto lg:py-20 lg:max-w-7xl">
        <div className="relative z-20  min-h-[90vh] flex items-center gap-0 text-white lg:flex-row flex-col-reverse">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 1, type: "spring", delay: 0.1 }}
            className="flex flex-col items-center w-full gap-10 lg:items-start lg:w-3/5"
          >
            <Image
              src={props?.image.url}
              alt={props?.image.altTag}
              width={500}
              height={300}
              priority={true}
              className="order-last w-2/3 mx-auto lg:hidden"
            />
            {props?.mobileImage && (
              <Image
                src={props?.mobileImage?.url}
                alt={props?.mobileImage?.altTag}
                width={500}
                height={300}
                priority={true}
                className="order-last w-full mx-auto lg:hidden"
              />
            )}
            {props?.topTitle && (
              <div
                className={clsx(
                  "-mb-10 text-2xl text-white font-bold",
                  props?.textShadow && "text-shadow-lg"
                )}
                style={
                  props?.textShadow
                    ? {
                        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                      }
                    : {}
                }
              >
                {props?.topTitle}
              </div>
            )}
            <h1
              className={clsx(
                "text-white",
                props?.textShadow && "text-shadow-lg"
              )}
              style={
                props?.textShadow
                  ? {
                      textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                    }
                  : {}
              }
            >
              {parse(props?.mainTitle)}
            </h1>
            <div
              className={clsx(
                "text-base lg:w-2/3 lg:text-2xl",
                props?.textShadow && "text-shadow-lg"
              )}
              style={
                props?.textShadow
                  ? {
                      textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                    }
                  : {}
              }
            >
              {props?.secondaryTitle}
            </div>
            {props?.countDownTimer && (
              <div>
                <LCountDownTimer
                  endDate={new Date(props?.countDownTimer?.endDate)}
                  description={props?.countDownTimer?.description}
                  hasArrowDownImage
                  hideArrowOnMobile
                />
              </div>
            )}
            {props?.button?.text && props?.button?.url && (
              <WButton
                text={props?.button.text}
                type="primary-red-btn"
                className="w-fit"
                onClick={() => {
                  props?.button?.url && onClick();
                  fbEvent("Schedule");
                }}
              />
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false }}
            transition={{ duration: 0.5, type: "spring", delay: 0.2 }}
            className="flex-shrink-0 mb-auto w-52 justify-self-start"
          ></motion.div>
        </div>
        <motion.img
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 1, type: "spring", delay: 0.3 }}
          src={props?.image.url}
          alt={props?.image.altTag}
          className="lg:absolute lg:bottom-0 lg:right-0 z-10 object-contain object-bottom w-[40vw] h-full hidden lg:block"
        />
        {props?.mobileImage && (
          <Image
            src={props?.mobileImage?.url}
            alt={props?.mobileImage?.altTag}
            width={500}
            height={300}
            priority={true}
            className="lg:hidden w-full h-full object-contain object-bottom"
          />
        )}
      </div>
      {props?.mobileImage ? (
        <>
          <Image
            src={props.image.url}
            alt={props.image.altTag}
            width={1200}
            height={800}
            priority={true}
            className="hidden lg:block"
            quality={85}
          />
          <Image
            src={props.mobileImage.url}
            alt={props.mobileImage.altTag}
            width={500}
            height={300}
            priority={true}
            className="block lg:hidden"
            quality={85}
          />
        </>
      ) : (
        <Image
          src={props.image.url}
          alt={props.image.altTag}
          width={1200}
          height={800}
          priority={true}
          sizes="(max-width: 768px) 100vw, 1200px"
          quality={85}
        />
      )}
    </div>
  );
};

export default LHeroSection;
